/**
 * Now Assist Skill Plugin - Main Entry Point
 *
 * This plugin processes NowAssistSkillConfig API calls and generates ServiceNow records.
 * The plugin has been modularized into separate files for better maintainability:
 *
 * - skill-plugin-helpers.ts: Utility functions
 * - skill-processor.ts: Main skill processing
 * - transform-logic.ts: ServiceNow → Fluent API transformation
 * - defaults.ts: All constants and default values
 */

import {
    CallExpressionShape,
    Plugin,
    type Context,
    unloadBuilder,
    type Record,
    type OutputFile,
    isGUID,
    ts,
    path,
    type Product,
} from '@servicenow/sdk-build-core'
import { TABLE_NAMES, SECURITY_TABLE_NAMES, VAR_SET, EXCLUDED_METRIC_CAPABILITY_IDS } from './defaults'
import { processSkill } from './skill-processor'
import { transformToShape } from './transform-logic'
import { sanitizeSkillName } from './skill-plugin-helpers'
import { ArrowFunctionShape } from '@servicenow/sdk-build-plugins'

// Destructure table names from TABLE_NAMES for easier usage
const {
    SN_NOWASSIST_SKILL_CONFIG,
    SN_NOWASSIST_SKILL_CONFIG_STATUS,
    SN_NOWASSIST_SKILL_CONFIG_VAR_SET,
    SN_NOWASSIST_SKILL_CONFIG_APPLICABILITY,
    SN_NOWASSIST_SKILL_CONFIG_UI,
    SYS_GENERATIVE_AI_PROMPT_CONFIG,
    SYS_GENERATIVE_AI_CONFIG,
    SYS_GENERATIVE_AI_VALIDATOR,
    SYS_GENERATIVE_AI_REQUEST_VALIDATOR,
    SYS_GENERATIVE_AI_RESPONSE_VALIDATOR,
    SYS_GEN_AI_FEATURE_MAPPING,
    SYS_GEN_AI_STRATEGY_MAPPING,
    SYS_GEN_AI_SKILL,
    SYS_GEN_AI_SKILL_APPLICABILITY,
    SYS_GEN_AI_SKILL_CONFIG,
    SYS_ONE_EXTEND_CAPABILITY,
    SYS_ONE_EXTEND_CAPABILITY_DEFINITION,
    SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE,
    SYS_ONE_EXTEND_DEFINITION_CONFIG,
    SYS_VARIABLE_VALUE,
    SYS_ONE_EXTEND_RESOURCE_MAPPING,
    SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING,
    SYS_ONE_EXTEND_RESOURCE_EDGE,
} = TABLE_NAMES

const { SYS_AGENT_ACCESS_ROLE_CONFIGURATION, SYS_AGENT_ACCESS_ROLE_MAPPING, SYS_USER_ROLE } = SECURITY_TABLE_NAMES

/**
 * Helper to extract table name and data fields from a Record() call expression AST node.
 * Returns null if the node is not a valid Record({ table, data }) call.
 */
function parseRecordCall(callExpr: ts.CallExpression): {
    tableName: string
    configObj: ts.ObjectLiteralExpression
    dataObj: ts.ObjectLiteralExpression | undefined
    filePath: string
} | null {
    const expression = callExpr.getExpression()
    if (!ts.Node.isIdentifier(expression) || expression.getText() !== 'Record') {
        return null
    }

    const args = callExpr.getArguments()
    if (args.length === 0) {
        return null
    }

    const configArg = args[0]
    if (!ts.Node.isObjectLiteralExpression(configArg)) {
        return null
    }

    const tableProperty = configArg.getProperty('table')
    if (!tableProperty || !ts.Node.isPropertyAssignment(tableProperty)) {
        return null
    }

    const tableValue = tableProperty.getInitializer()
    if (!tableValue || !ts.Node.isStringLiteral(tableValue)) {
        return null
    }

    const dataProperty = configArg.getProperty('data')
    let dataObj: ts.ObjectLiteralExpression | undefined
    if (dataProperty && ts.Node.isPropertyAssignment(dataProperty)) {
        const init = dataProperty.getInitializer()
        if (init && ts.Node.isObjectLiteralExpression(init)) {
            dataObj = init
        }
    }

    return {
        tableName: tableValue.getLiteralValue(),
        configObj: configArg,
        dataObj,
        filePath: callExpr.getSourceFile().getFilePath(),
    }
}

/**
 * Helper to get a string literal value from an object property.
 */
function getStringField(obj: ts.ObjectLiteralExpression, fieldName: string): string | undefined {
    const prop = obj.getProperty(fieldName)
    if (!prop || !ts.Node.isPropertyAssignment(prop)) {
        return undefined
    }
    const init = prop.getInitializer()
    if (!init || !ts.Node.isStringLiteral(init)) {
        return undefined
    }
    return init.getLiteralValue()
}

/**
 * Detect Record API tool record files in the project, excluding metric records.
 *
 * Uses a two-pass approach:
 *   Pass 1: Identify tool resource mapping IDs from sys_one_extend_resource_mapping records.
 *           A mapping is a tool (not a metric) if its resource_capability is NOT in
 *           EXCLUDED_METRIC_CAPABILITY_IDS. This includes both tools and the skill's
 *           self-mapping — both are stale when Fluent API handles the skill.
 *   Pass 2: For sys_one_extend_resource_attribute_mapping, include only files whose
 *           resource_mapping references a tool mapping ID. Edge files are always tool-related.
 *
 * Performance: Source files are pre-filtered by filename before AST traversal. Only files
 * containing `sys_one_extend_resource_mapping`, `sys_one_extend_resource_attribute_mapping`,
 * or `sys_one_extend_resource_edge` in their path are traversed — all other project files
 * (business rules, ACLs, client scripts, etc.) are skipped entirely.
 *
 * @param compiler - The compiler (ts.Project) for AST access
 * @returns File paths containing Record API tool records (excluding metrics)
 */
function detectRecordApiToolRecords(
    compiler: { getSourceFiles(): ts.SourceFile[] },
    skillCapabilityId?: string
): string[] {
    const toolFilePaths = new Set<string>()

    // Metric mapping IDs — used to exclude metric attribute mappings in pass 2
    const metricMappingIds = new Set<string>()

    // Tool mapping IDs for this skill — used to scope attribute mappings and edges in pass 2
    const skillToolMappingIds = new Set<string>()

    // Candidates from child tables — resolved in pass 2
    const attributeMappingCandidates: { filePath: string; resourceMapping: string }[] = []
    const edgeCandidates: { filePath: string; fromResource: string; toResource: string }[] = []

    // Only scan files whose path contains one-extend table names — avoids AST traversal
    // on unrelated files (e.g., business rules, ACLs, client scripts, etc.)
    // Also filter to only include .now.ts files in the fluent directory as a fallback
    // (users can move tools to any file path, but this covers the common case)

    const sourceFiles = compiler.getSourceFiles().filter((sf) => {
        const fp = sf.getFilePath()
        // Must be a .now.ts file in the fluent directory
        if (!fp.includes('/fluent/') || !fp.endsWith('.now.ts')) {
            return false
        }
        return (
            fp.includes('sys_one_extend_resource_mapping') ||
            fp.includes('sys_one_extend_resource_attribute_mapping') ||
            fp.includes('sys_one_extend_resource_edge')
        )
    })

    // ── Pass 1: Scan matching files, classify records ──
    for (const sourceFile of sourceFiles) {
        const callExpressions = sourceFile.getDescendantsOfKind(ts.SyntaxKind.CallExpression)

        for (const callExpr of callExpressions) {
            const parsed = parseRecordCall(callExpr)
            if (!parsed) {
                continue
            }

            const { tableName, configObj, dataObj, filePath } = parsed

            if (tableName === SYS_ONE_EXTEND_RESOURCE_MAPPING && dataObj) {
                const resourceCapability = getStringField(dataObj, 'resource_capability')
                const parentCapability = getStringField(dataObj, 'parent_capability')

                // Skip self-mapping (skill maps to itself as root of tool graph)
                if (resourceCapability && parentCapability && resourceCapability === parentCapability) {
                    continue
                }

                // If skillCapabilityId is provided, only include mappings for this skill
                if (skillCapabilityId && parentCapability !== skillCapabilityId) {
                    continue
                }

                if (resourceCapability && EXCLUDED_METRIC_CAPABILITY_IDS.has(resourceCapability)) {
                    // Track metric mapping IDs to exclude their attribute mappings in pass 2
                    const $idProp = configObj.getProperty('$id')
                    if ($idProp && ts.Node.isPropertyAssignment($idProp)) {
                        const idInit = $idProp.getInitializer()
                        if (idInit && ts.Node.isElementAccessExpression(idInit)) {
                            const arg = idInit.getArgumentExpression()
                            if (arg && ts.Node.isStringLiteral(arg)) {
                                metricMappingIds.add(arg.getLiteralValue())
                            }
                        }
                    }
                } else {
                    // Non-metric: tool or self-mapping — include
                    toolFilePaths.add(filePath)
                    // Track this mapping's $id for scoping child records
                    const $idProp = configObj.getProperty('$id')
                    if ($idProp && ts.Node.isPropertyAssignment($idProp)) {
                        const idInit = $idProp.getInitializer()
                        if (idInit && ts.Node.isElementAccessExpression(idInit)) {
                            const arg = idInit.getArgumentExpression()
                            if (arg && ts.Node.isStringLiteral(arg)) {
                                skillToolMappingIds.add(arg.getLiteralValue())
                            }
                        }
                    }
                }
            } else if (tableName === SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING && dataObj) {
                const resourceMapping = getStringField(dataObj, 'resource_mapping')
                if (resourceMapping) {
                    attributeMappingCandidates.push({ filePath, resourceMapping })
                }
            } else if (tableName === SYS_ONE_EXTEND_RESOURCE_EDGE && dataObj) {
                const fromResource = getStringField(dataObj, 'from_resource') || ''
                const toResource = getStringField(dataObj, 'to_resource') || ''
                edgeCandidates.push({ filePath, fromResource, toResource })
            }
        }
    }

    // ── Pass 2: Include attribute mappings and edges scoped to this skill ──
    // If skillCapabilityId was provided but no resource mappings matched this skill,
    // then this skill has no Record API tool files — skip child record scanning entirely.
    if (skillCapabilityId && skillToolMappingIds.size === 0) {
        return [...toolFilePaths]
    }

    for (const { filePath, resourceMapping } of attributeMappingCandidates) {
        if (metricMappingIds.has(resourceMapping)) {
            continue
        }
        // If we have skill-scoped mapping IDs, only include attr mappings for this skill
        if (skillCapabilityId && !skillToolMappingIds.has(resourceMapping)) {
            continue
        }
        toolFilePaths.add(filePath)
    }

    for (const { filePath, fromResource, toResource } of edgeCandidates) {
        // If we have skill-scoped mapping IDs, only include edges for this skill
        if (skillCapabilityId) {
            if (!skillToolMappingIds.has(fromResource) && !skillToolMappingIds.has(toResource)) {
                continue
            }
        }
        toolFilePaths.add(filePath)
    }

    return [...toolFilePaths]
}

/**
 * Plugin Definition
 * Combines all table configurations and defines the transformation logic
 */
export const NowAssistSkillPlugin = Plugin.create({
    name: 'NowAssistSkillPlugin',
    docs: [
        {
            apiName: 'NowAssistSkillConfig',
            tags: ['sn_nowassist_skill_config'],
        },
    ],
    records: {
        // User's $id maps to this record, controlling identity via Now.ID['my_skill']
        // No coalesce needed since uniqueness is determined by user-provided $id
        [SN_NOWASSIST_SKILL_CONFIG]: {
            coalesce: ['skill_id'],
            relationships: {
                // SYS_ONE_EXTEND_CAPABILITY is the parent record that skill_id references
                // Using inverse: true since SN_NOWASSIST_SKILL_CONFIG.skill_id points TO capability
                [SYS_ONE_EXTEND_CAPABILITY]: {
                    via: 'skill_id',
                    inverse: true,
                    descendant: true,
                    relationships: {
                        // Capability Definition (defines HOW skill operates)
                        [SYS_ONE_EXTEND_CAPABILITY_DEFINITION]: {
                            via: 'capability',
                            descendant: true,
                            relationships: {
                                [SYS_ONE_EXTEND_DEFINITION_CONFIG]: {
                                    via: 'definition',
                                    descendant: true,
                                },
                                [SYS_GENERATIVE_AI_CONFIG]: {
                                    via: 'definition',
                                    descendant: true,
                                    relationships: {
                                        [SYS_GENERATIVE_AI_PROMPT_CONFIG]: {
                                            via: 'ai_config',
                                            descendant: true,
                                        },
                                    },
                                },
                                // Validators (preprocessors/postprocessors)
                                [SYS_GENERATIVE_AI_REQUEST_VALIDATOR]: {
                                    via: 'document_id',
                                    descendant: true,
                                    relationships: {
                                        [SYS_GENERATIVE_AI_VALIDATOR]: {
                                            via: 'validator',
                                            inverse: true,
                                            descendant: true,
                                        },
                                    },
                                },
                                [SYS_GENERATIVE_AI_RESPONSE_VALIDATOR]: {
                                    via: 'definition',
                                    descendant: true,
                                    relationships: {
                                        [SYS_GENERATIVE_AI_VALIDATOR]: {
                                            via: 'validator',
                                            inverse: true,
                                            descendant: true,
                                        },
                                    },
                                },
                            },
                        },
                        // Definition Attributes (input/output fields)
                        [SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE]: {
                            via: 'capability',
                            descendant: true,
                        },
                        // NAP Deployment
                        [SYS_GEN_AI_FEATURE_MAPPING]: {
                            via: 'document',
                            descendant: true,
                            relationships: {
                                [SYS_GEN_AI_STRATEGY_MAPPING]: {
                                    via: 'feature',
                                    descendant: true,
                                },
                            },
                        },
                        // Tool tables: resource mappings, attribute mappings, edges
                        // Filter out metric resource mapping records
                        [SYS_ONE_EXTEND_RESOURCE_MAPPING]: {
                            via: 'parent_capability',
                            descendant: true,
                            relationships: {
                                [SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING]: {
                                    via: 'resource_mapping',
                                    descendant: true,
                                },
                                [SYS_ONE_EXTEND_RESOURCE_EDGE]: {
                                    via: 'from_resource',
                                    descendant: true,
                                },
                                [SYS_ONE_EXTEND_CAPABILITY]: {
                                    via: 'resource_capability',
                                    descendant: true,
                                    inverse: true,
                                },
                            },
                        },
                        [SYS_GEN_AI_SKILL]: {
                            via: 'skill_document',
                            descendant: true,
                            relationships: {
                                [SYS_GEN_AI_SKILL_APPLICABILITY]: {
                                    via: 'skill_id',
                                    descendant: true,
                                },
                                [SYS_GEN_AI_SKILL_CONFIG]: {
                                    via: 'skill_id',
                                    descendant: true,
                                },
                            },
                        },
                    },
                },
                // Direct children of SN_NOWASSIST_SKILL_CONFIG
                [SN_NOWASSIST_SKILL_CONFIG_STATUS]: {
                    via: 'skill_config',
                    descendant: true,
                },
                [SN_NOWASSIST_SKILL_CONFIG_VAR_SET]: {
                    via: 'skill_config',
                    descendant: true,
                    relationships: {
                        [SYS_VARIABLE_VALUE]: {
                            via: 'document_key',
                            descendant: true,
                            relationships: {
                                sys_ui_action: {
                                    via: [{ parent: ['value', '(.+)'], child: ['sys_id', '(.+)'] }],
                                    descendant: true,
                                    relationships: {
                                        sys_ux_form_action: {
                                            via: 'ui_action',
                                            descendant: true,
                                        },
                                    },
                                },
                            },
                        },
                    },
                },
                [SN_NOWASSIST_SKILL_CONFIG_APPLICABILITY]: {
                    via: 'skill_config',
                    descendant: true,
                },
                [SN_NOWASSIST_SKILL_CONFIG_UI]: {
                    via: 'skill_config',
                    descendant: true,
                },
                // Security: ACL and role configuration
                sys_security_acl: {
                    via: [{ parent: ['internal_name', '\\.(.*)'], child: ['name', '\\.(.*)'] }],
                    descendant: true,
                    relationships: {
                        sys_security_acl_role: {
                            via: 'sys_security_acl',
                            descendant: true,
                        },
                    },
                },
                [SYS_AGENT_ACCESS_ROLE_CONFIGURATION]: {
                    via: 'agent',
                    descendant: true,
                    relationships: {
                        [SYS_AGENT_ACCESS_ROLE_MAPPING]: {
                            via: 'agent_access_config',
                            descendant: true,
                            relationships: {
                                [SYS_USER_ROLE]: {
                                    via: 'role',
                                    inverse: true,
                                },
                            },
                        },
                    },
                },
            },
            async toShape(record, { descendants, transform, diagnostics, database }) {
                // Use the imported transform logic
                // Pass database so transform can resolve capabilities from other skills (same-scope Skill-as-Tool)
                return transformToShape(record, descendants, transform, diagnostics, database)
            },
            async toFile(record, context) {
                const { config, transform, descendants, self, diagnostics, database } = context

                // Get ALL descendants including DELETE records. The SDK's `descendants`
                // parameter only contains INSERT_OR_UPDATE records (filtered at plugin.ts:1280),
                // but DELETE descendants are still marked as "handled" (plugin.ts:1286),
                // preventing RecordPlugin from serializing them. Use self.getDescendants()
                // to retrieve the unfiltered list so we can serialize DELETE records ourselves.
                // Note: Some DELETE records (e.g., resource_edge with only internal_name in
                // coalesce key) won't be found by getDescendants due to missing FK fields —
                // those fall through to RecordPlugin's catch-all handler and are serialized there.
                const allDescendants = self.getDescendants(record, database)

                // Get the skill name and sys_id
                const skillName = record.get('name')?.toString().getValue()
                const sysId = record.getId().getValue()

                if (!skillName) {
                    // No name, use default serialization
                    return { success: false }
                }

                // Generate the internal_name with the actual sys_id
                const sanitizedName = sanitizeSkillName(skillName)
                const internalName = `${sanitizedName}.${sysId}`

                // Serialize with the updated internal_name
                const recordBuilder = unloadBuilder(config)
                const updateName = await transform.getUpdateName(record)
                const builder = recordBuilder.record(record, updateName)

                // Iterate through entries and add internal_name field
                record
                    .entries()
                    .sort(([a], [b]) => a.localeCompare(b))
                    .forEach(([prop, shape]) => {
                        builder.field(prop, shape)
                    })

                // Add the generated internal_name field (not in record entries)
                builder.field('internal_name', internalName)

                // Helper function to serialize a descendant record
                const serializeDescendant = async (descendantRecord: Record) => {
                    const descUpdateName = await transform.getUpdateName(descendantRecord)
                    const descBuilder = unloadBuilder(config)
                    const descRecordBuilder = descBuilder.record(descendantRecord, descUpdateName)

                    descendantRecord
                        .entries()
                        .sort(([a], [b]) => a.localeCompare(b))
                        .forEach(([prop, shape]) => {
                            descRecordBuilder.field(prop, shape)
                        })

                    return {
                        source: descendantRecord,
                        name: `${descUpdateName}.xml`,
                        category: descendantRecord.getInstallCategory(),
                        content: descBuilder.end(),
                    }
                }

                // Process all descendant records
                const descendantFiles: OutputFile[] = []

                // Process SYS_ONE_EXTEND_CAPABILITY (inverse relationship)
                for (const rec of descendants.query(SYS_ONE_EXTEND_CAPABILITY)) {
                    descendantFiles.push(await serializeDescendant(rec))
                }

                // Process capability definitions and their children
                for (const rec of descendants.query(SYS_ONE_EXTEND_CAPABILITY_DEFINITION)) {
                    // Use self.recordToFile to allow custom toFile handlers to run
                    const { self: _, ...contextWithoutSelf } = context
                    const result = await self.recordToFile(rec, contextWithoutSelf)
                    if (result.success && result.value) {
                        const files = Array.isArray(result.value) ? result.value : [result.value]
                        descendantFiles.push(...files)
                    } else {
                        descendantFiles.push(await serializeDescendant(rec))
                    }
                }

                // Process definition configs
                for (const rec of descendants.query(SYS_ONE_EXTEND_DEFINITION_CONFIG)) {
                    descendantFiles.push(await serializeDescendant(rec))
                }

                // Process definition attributes
                for (const rec of descendants.query(SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE)) {
                    descendantFiles.push(await serializeDescendant(rec))
                }

                // Process AI configs and prompt configs
                for (const rec of descendants.query(SYS_GENERATIVE_AI_CONFIG)) {
                    descendantFiles.push(await serializeDescendant(rec))
                }
                for (const rec of descendants.query(SYS_GENERATIVE_AI_PROMPT_CONFIG)) {
                    descendantFiles.push(await serializeDescendant(rec))
                }

                // Process validators
                for (const rec of descendants.query(SYS_GENERATIVE_AI_VALIDATOR)) {
                    descendantFiles.push(await serializeDescendant(rec))
                }
                for (const rec of descendants.query(SYS_GENERATIVE_AI_REQUEST_VALIDATOR)) {
                    descendantFiles.push(await serializeDescendant(rec))
                }
                for (const rec of descendants.query(SYS_GENERATIVE_AI_RESPONSE_VALIDATOR)) {
                    descendantFiles.push(await serializeDescendant(rec))
                }

                // Process NAP deployment records
                for (const rec of descendants.query(SYS_GEN_AI_FEATURE_MAPPING)) {
                    descendantFiles.push(await serializeDescendant(rec))
                }
                for (const rec of descendants.query(SYS_GEN_AI_STRATEGY_MAPPING)) {
                    descendantFiles.push(await serializeDescendant(rec))
                }
                for (const rec of descendants.query(SYS_GEN_AI_SKILL)) {
                    descendantFiles.push(await serializeDescendant(rec))
                }
                for (const rec of descendants.query(SYS_GEN_AI_SKILL_APPLICABILITY)) {
                    descendantFiles.push(await serializeDescendant(rec))
                }
                for (const rec of descendants.query(SYS_GEN_AI_SKILL_CONFIG)) {
                    descendantFiles.push(await serializeDescendant(rec))
                }

                // Process sn_nowassist_skill_config_status
                for (const rec of descendants.query(SN_NOWASSIST_SKILL_CONFIG_STATUS)) {
                    descendantFiles.push(await serializeDescendant(rec))
                }

                // Process sn_nowassist_skill_config_var_set and its children
                for (const varSetRec of descendants.query(SN_NOWASSIST_SKILL_CONFIG_VAR_SET)) {
                    descendantFiles.push(await serializeDescendant(varSetRec))
                }

                // Process sys_variable_value (children of var_set)
                // Use self.recordToFile to allow custom toFile handlers to run
                for (const rec of descendants.query(SYS_VARIABLE_VALUE)) {
                    const { self: _, ...contextWithoutSelf } = context
                    const result = await self.recordToFile(rec, contextWithoutSelf)
                    if (result.success && result.value) {
                        const files = Array.isArray(result.value) ? result.value : [result.value]
                        descendantFiles.push(...files)
                    } else {
                        descendantFiles.push(await serializeDescendant(rec))
                    }
                }

                // Process sn_nowassist_skill_config_applicability
                for (const rec of descendants.query(SN_NOWASSIST_SKILL_CONFIG_APPLICABILITY)) {
                    descendantFiles.push(await serializeDescendant(rec))
                }

                // Process sn_ns_skill_config_ui
                for (const rec of descendants.query(SN_NOWASSIST_SKILL_CONFIG_UI)) {
                    descendantFiles.push(await serializeDescendant(rec))
                }

                // Process sys_ui_action and sys_ux_form_action
                for (const rec of descendants.query('sys_ui_action')) {
                    descendantFiles.push(await serializeDescendant(rec))
                }
                for (const rec of descendants.query('sys_ux_form_action')) {
                    descendantFiles.push(await serializeDescendant(rec))
                }

                // Process sys_security_acl and sys_security_acl_role
                for (const rec of descendants.query('sys_security_acl')) {
                    descendantFiles.push(await serializeDescendant(rec))
                }
                for (const rec of descendants.query('sys_security_acl_role')) {
                    descendantFiles.push(await serializeDescendant(rec))
                }

                // Also emit DELETE XMLs for deleted sys_security_acl_role records
                // (descendants only has INSERT_OR_UPDATE, so DELETE records are missed)
                const skillAclIds = new Set(descendants.query('sys_security_acl').map((r) => r.getId().getValue()))
                for (const rec of database.query('sys_security_acl_role')) {
                    if (rec.getAction() === 'DELETE') {
                        const aclRef = rec.get('sys_security_acl')?.toString().getValue()
                        if (aclRef && skillAclIds.has(aclRef)) {
                            descendantFiles.push(await serializeDescendant(rec))
                        }
                    }
                }

                // Process sys_agent_access_role_configuration
                for (const rec of descendants.query(SYS_AGENT_ACCESS_ROLE_CONFIGURATION)) {
                    descendantFiles.push(await serializeDescendant(rec))
                }

                // Process sys_agent_access_role_mapping (M2M children of the role config above).
                // These are emitted by security-processor.ts as part of dual-write; without this
                // loop the records exist in the in-memory graph but never get written to disk.
                for (const rec of descendants.query(SYS_AGENT_ACCESS_ROLE_MAPPING)) {
                    descendantFiles.push(await serializeDescendant(rec))
                }

                // Validate: Fluent API tools must not coexist with Record API tool records.
                // When transform produces partial success (unsupported tools), ALL tool records
                // go to Record API. If the user then adds tools in the Fluent API, both sources
                // would produce conflicting records. Detect this and error early.
                const allResourceMappings = descendants.query(SYS_ONE_EXTEND_RESOURCE_MAPPING)
                let hasFluentApiTools = false
                let hasRecordApiTools = false
                const recordApiToolFilePaths = new Set<string>()

                for (const mapping of allResourceMappings) {
                    const resourceCapId = mapping.get('resource_capability')?.toString().getValue()
                    const parentCapId = mapping.get('parent_capability')?.toString().getValue()

                    // Skip self-mapping — always exists regardless of tools
                    if (resourceCapId === parentCapId) {
                        continue
                    }

                    // Skip metric records — only tools should be flagged
                    if (resourceCapId && EXCLUDED_METRIC_CAPABILITY_IDS.has(resourceCapId)) {
                        continue
                    }

                    // Determine if this record came from Fluent API or Record API
                    // Fluent API tools originate from ObjectLiteralExpression (tool definitions in tools arrow function)
                    // Record API tools originate from Record(...) call expressions or XML files
                    let isFluentApiTool = false
                    let isRecordApiTool = false
                    try {
                        const originalNode = mapping.getOriginalNode()

                        if (ts.Node.isObjectLiteralExpression(originalNode)) {
                            // Fluent API tool - defined in the tools arrow function
                            isFluentApiTool = true
                        } else if (ts.Node.isCallExpression(originalNode)) {
                            // Check if it's a Record() call
                            const expression = originalNode.getExpression()
                            if (ts.Node.isIdentifier(expression) && expression.getText() === 'Record') {
                                // Record API tool
                                isRecordApiTool = true
                            }
                        }
                    } catch {
                        // Source is a File (XML), not a node — treat as Record API
                        isRecordApiTool = true
                    }

                    if (isRecordApiTool) {
                        hasRecordApiTools = true
                        try {
                            recordApiToolFilePaths.add(mapping.getOriginalFilePath())
                        } catch {
                            // Could not resolve file path — skip
                        }
                    } else if (isFluentApiTool) {
                        hasFluentApiTools = true
                    }
                }

                if (hasFluentApiTools && hasRecordApiTools) {
                    const rootDir = context.project.getRootDir()
                    const relativeFiles = [...recordApiToolFilePaths].map((f) => path.relative(rootDir, f))
                    const fileList = relativeFiles.map((f) => `  - ${f}`).join('\n')
                    diagnostics.error(
                        record,
                        'Build conflict: Tools are defined in both the Fluent API and as Record API files.\n' +
                            'The following Record API files contain tool records:\n' +
                            fileList +
                            '\n\n' +
                            'To resolve this conflict:\n' +
                            '  1. If these files are leftover from a previous transform that included unsupported tools,\n' +
                            '     delete the files listed above and rebuild.\n' +
                            '  2. If unsupported tools (e.g., DocIntel, PI, Retriever) are still present in the skill,\n' +
                            '     remove them from the Now Assist Skill Kit UI, then re-transform before defining\n' +
                            '     tools in the Fluent API. Alternatively, remove the `tools` property from the\n' +
                            '     Fluent API to continue using Record API tools.'
                    )
                }

                // Process tool records (resource mappings, attribute mappings, edges)
                for (const rec of allResourceMappings) {
                    descendantFiles.push(await serializeDescendant(rec))
                }
                for (const rec of descendants.query(SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING)) {
                    descendantFiles.push(await serializeDescendant(rec))
                }
                for (const rec of descendants.query(SYS_ONE_EXTEND_RESOURCE_EDGE)) {
                    descendantFiles.push(await serializeDescendant(rec))
                }

                // Serialize DELETE descendant records.
                // The SDK's `descendants` only contains INSERT_OR_UPDATE records, but
                // `allDescendants` (from self.getDescendants) includes DELETE records too.
                // We serialize any DELETE descendant that wasn't already serialized above.
                const serializedIds = new Set(
                    descendantFiles.map((f) => (f.source as Record)?.getId()?.getValue()).filter(Boolean)
                )
                for (const rec of allDescendants) {
                    if (rec.getAction() === 'DELETE' && !serializedIds.has(rec.getId().getValue())) {
                        descendantFiles.push(await serializeDescendant(rec))
                    }
                }

                return {
                    success: true,
                    value: [
                        {
                            source: record,
                            name: `${updateName}.xml`,
                            category: record.getInstallCategory(),
                            content: recordBuilder.end(),
                        },
                        ...descendantFiles,
                    ],
                }
            },
        },

        // ===========================
        // sys_one_extend_* Tables
        // ===========================
        [SYS_ONE_EXTEND_CAPABILITY]: {
            // No coalesce needed - identity managed via derived explicitId in skill-processor.ts
            // ('{skillKey}__capability'). As platform allows duplicate/mutable names.
            //
            // During transform, the capability's explicit ID is registered in keys.ts via the
            // toFile handler (see above). This ensures the capability's sys_id is preserved
            // through transform → build → deploy cycles, preventing orphaned skills.
        },
        [SYS_ONE_EXTEND_DEFINITION_CONFIG]: {
            coalesce: ['definition', 'capability'],
        },
        [SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE]: {
            // No coalesce needed - identity managed via required $id (explicitId) on inputs/outputs.
            // Using 'name' in coalesce caused duplicates when users renamed attributes, since
            // the internal name changes (e.g., 'userRequest' → 'userrequest' → 'user_request').
            // The $id provides stable sys_id identity through rename operations.
        },

        // ===========================
        // sys_gen_ai_* Tables
        // ===========================
        [SYS_GENERATIVE_AI_CONFIG]: {},
        [SYS_GENERATIVE_AI_PROMPT_CONFIG]: {
            coalesce: ['ai_config'],
        },
        [SYS_GENERATIVE_AI_VALIDATOR]: {
            // No coalesce needed - identity managed via explicitId in provider-processor.ts
            // ('{definitionId}_request' or '{definitionId}_response'). This ensures validators
            // are updated (not duplicated) when provider names change.
        },
        [SYS_GENERATIVE_AI_REQUEST_VALIDATOR]: {
            coalesce: ['document_id', 'document_table', 'validator'],
        },
        [SYS_GENERATIVE_AI_RESPONSE_VALIDATOR]: {
            coalesce: ['definition', 'definition_table', 'validator'],
        },
        [SYS_GEN_AI_FEATURE_MAPPING]: {
            coalesce: ['feature_name', 'document'],
        },
        [SYS_GEN_AI_STRATEGY_MAPPING]: {
            coalesce: ['strategy', 'feature'],
        },
        [SYS_GEN_AI_SKILL]: {
            coalesce: ['skill_document', 'skill_table'],
        },
        [SYS_GEN_AI_SKILL_APPLICABILITY]: {
            coalesce: (properties) => ({
                skill_id: properties.get('skill_id').getValue() as string,
            }),
        },
        [SYS_GEN_AI_SKILL_CONFIG]: {
            coalesce: (properties) => ({
                skill_id: properties.get('skill_id').getValue() as string,
            }),
        },

        // ===========================
        // sn_nowassist_* Tables
        // ===========================
        [SN_NOWASSIST_SKILL_CONFIG_STATUS]: {
            coalesce: ['skill_config'],
        },
        [SN_NOWASSIST_SKILL_CONFIG_APPLICABILITY]: {
            coalesce: ['skill_config'],
        },
        [SN_NOWASSIST_SKILL_CONFIG_VAR_SET]: {
            coalesce: ['name', 'skill_config', 'config_type'],
        },
        // ROLE MASK TABLE
        [SYS_AGENT_ACCESS_ROLE_CONFIGURATION]: {
            coalesce: ['agent', 'agent_table', 'action'],
        },
        [SYS_AGENT_ACCESS_ROLE_MAPPING]: {
            coalesce: ['agent_access_config', 'role'],
        },

        // ===========================
        // Tool Tables
        // ===========================
        [SYS_ONE_EXTEND_RESOURCE_MAPPING]: {
            coalesce: (properties): { [key: string]: string } => {
                const parentCap = properties.get('parent_capability')?.getValue() as string
                const resourceCap = properties.get('resource_capability')?.getValue() as string

                // Skill's self-mapping (parent_capability === resource_capability)
                if (parentCap && parentCap === resourceCap) {
                    return {
                        parent_capability: parentCap,
                        resource_capability: resourceCap,
                    }
                }

                // Tool records: use parent_capability + resource_capability + resource_name
                // All three together uniquely identify a tool within a skill.
                // (explicitId from $id is the primary identity — this is the fallback)
                return {
                    parent_capability: parentCap,
                    resource_capability: resourceCap,
                    resource_name: properties.get('resource_name')?.getValue() as string,
                }
            },
        },
        [SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING]: {
            coalesce: ['resource_mapping', 'resource_attribute_name'],
        },
        [SYS_ONE_EXTEND_RESOURCE_EDGE]: {
            coalesce: ['internal_name'],
        },

        // Custom toFile handler for capability definitions to resolve scriptId in metadata
        [SYS_ONE_EXTEND_CAPABILITY_DEFINITION]: {
            coalesce: ['capability', 'api'],
            async toFile(record, { config, database, transform, descendants, diagnostics }) {
                // Check if this record has metadata with scriptId that needs resolution
                const metadataField = record.get('metadata')

                if (!metadataField || !metadataField.getValue()) {
                    // No metadata, use default serialization
                    return { success: false }
                }

                const metadataStr = metadataField.toString().getValue()

                // Try to parse the metadata JSON
                let metadata: {
                    scriptId?: string
                    scriptFunctionName?: string
                    scriptIncludeName?: string
                    [key: string]: unknown
                }
                try {
                    metadata = JSON.parse(metadataStr)
                } catch (e) {
                    // Invalid JSON, use default serialization
                    return { success: false }
                }

                // Validate and process scriptId
                if (metadata.scriptId) {
                    const scriptIdValue = String(metadata.scriptId)

                    if (isGUID(scriptIdValue)) {
                        // Valid sys_id GUID - use as-is, no resolution needed
                        // Continue with normal serialization below
                    } else {
                        // Not a GUID - check if it's a valid Now.ID key
                        const scriptIdKey = scriptIdValue

                        // Look up Script Include by key in the database
                        const scriptIncludeRecords = database.query('sys_script_include')
                        const scriptInclude = scriptIncludeRecords.find((r: Record) => {
                            // Check if this record has the matching $id key
                            const recordId = r.getId()
                            const recordIdKey = recordId.getNowIdKey?.()
                            return recordIdKey === scriptIdKey
                        })

                        if (!scriptInclude) {
                            // Neither valid GUID nor resolvable key - report error and use default serialization
                            diagnostics.error(
                                record,
                                `Invalid scriptId '${scriptIdValue}' in capability definition metadata. ` +
                                    `Must be either a valid sys_id (GUID) or a resolvable Now.ID key like Now.ID['script_include_name']. ` +
                                    `The invalid scriptId will be preserved in the XML and may cause runtime errors.`
                            )
                            return { success: false }
                        }

                        // Found matching Script Include - resolve key to sys_id
                        // Replace key with actual sys_id
                        metadata.scriptId = scriptInclude.getId().getValue()
                        const resolvedMetadataStr = JSON.stringify(metadata)

                        // Serialize with the updated metadata
                        const recordBuilder = unloadBuilder(config)
                        const updateName = await transform.getUpdateName(record)
                        const builder = recordBuilder.record(record, updateName)

                        // Iterate through entries and override metadata field
                        record
                            .entries()
                            .sort(([a], [b]) => a.localeCompare(b))
                            .forEach(([prop, shape]) => {
                                if (prop === 'metadata') {
                                    // Use resolved metadata with sys_id instead of key
                                    builder.field(prop, resolvedMetadataStr)
                                } else {
                                    builder.field(prop, shape)
                                }
                            })

                        // IMPORTANT: Query and serialize descendant definition configs
                        // The custom toFile bypasses normal descendant processing, so we must handle it manually
                        const definitionConfigs = descendants.query(SYS_ONE_EXTEND_DEFINITION_CONFIG)
                        const descendantFiles: OutputFile[] = []

                        for (const configRecord of definitionConfigs) {
                            const configUpdateName = await transform.getUpdateName(configRecord)
                            const configBuilder = unloadBuilder(config)
                            const configRecordBuilder = configBuilder.record(configRecord, configUpdateName)

                            // Serialize all fields of the definition config
                            configRecord
                                .entries()
                                .sort(([a], [b]) => a.localeCompare(b))
                                .forEach(([prop, shape]) => {
                                    configRecordBuilder.field(prop, shape)
                                })

                            descendantFiles.push({
                                source: configRecord,
                                name: `${configUpdateName}.xml`,
                                category: configRecord.getInstallCategory(),
                                content: configBuilder.end(),
                            })
                        }

                        return {
                            success: true,
                            value: [
                                {
                                    source: record,
                                    name: `${updateName}.xml`,
                                    category: record.getInstallCategory(),
                                    content: recordBuilder.end(),
                                },
                                ...descendantFiles,
                            ],
                        }
                    }
                }

                // If no resolution needed or record not found, use default serialization
                return { success: false }
            },
        },

        // Custom toFile handler for variable values to resolve UIAction references
        [SYS_VARIABLE_VALUE]: {
            coalesce: ['document_key', 'variable'],
            async toFile(record, { config, database, transform }) {
                // Check if this is a UI Action ID variable
                const variable = record.get('variable')?.toString().getValue()
                const value = record.get('value')?.toString().getValue()

                // Only process if it's a UI Action ID variable and value exists
                if (variable === VAR_SET.UI_ACTION_ID && value && !isGUID(value)) {
                    // This is the UI_ACTION_ID variable, try to resolve the sys_id
                    const uiActionRecords = database.query('sys_ui_action')
                    // Try to find the UI Action by matching either:
                    // 1. The sys_id (GUID) directly - when ExplicitKey is used
                    // 2. The key string - for backward compatibility
                    const uiAction = uiActionRecords.find((r: Record) => {
                        const recordId = r.getId()
                        const recordIdValue = recordId.getValue()
                        const recordIdKey = recordId.getNowIdKey?.()

                        // Match by sys_id (GUID) or by key
                        return recordIdValue === value || recordIdKey === value
                    })

                    if (uiAction) {
                        // Replace key with actual sys_id
                        const resolvedValue = uiAction.getId().getValue()

                        // Serialize with the updated value
                        const recordBuilder = unloadBuilder(config)
                        const updateName = await transform.getUpdateName(record)
                        const builder = recordBuilder.record(record, updateName)

                        // Iterate through entries and override value field
                        record
                            .entries()
                            .sort(([a], [b]) => a.localeCompare(b))
                            .forEach(([prop, shape]) => {
                                if (prop === 'value') {
                                    // Use resolved sys_id instead of key
                                    builder.field(prop, resolvedValue)
                                } else {
                                    builder.field(prop, shape)
                                }
                            })

                        return {
                            success: true,
                            value: {
                                source: record,
                                name: `${updateName}.xml`,
                                category: record.getInstallCategory(),
                                content: recordBuilder.end(),
                            },
                        }
                    }
                }

                // If no resolution needed or record not found, use default serialization
                return { success: false }
            },
        },

        sys_ui_action: {
            // No coalesce needed - identity managed via $id (Now.ID) explicitId from UIActionConfig
        },
        sys_ux_form_action: {
            coalesce: ['ui_action'],
        },
    },

    shapes: [
        {
            shape: CallExpressionShape,
            fileTypes: ['fluent'],
            async toRecord(callExpression, context: Context) {
                const { diagnostics, compiler } = context
                const calleeName = callExpression.getCallee()?.toString()

                // Only accept NowAssistSkillConfig API
                if (calleeName !== 'NowAssistSkillConfig') {
                    return { success: false }
                }

                // Two-argument API: Arg 1 = definition, Arg 2 = prompt configuration
                const definitionArg = callExpression.getArgument(0)
                if (!definitionArg?.isObject()) {
                    diagnostics.error(
                        callExpression,
                        `${calleeName} requires a definition object as the first argument`
                    )
                    return { success: false }
                }

                const promptConfigArg = callExpression.getArgument(1)
                if (!promptConfigArg?.isObject()) {
                    diagnostics.error(
                        callExpression,
                        `${calleeName} requires a prompt configuration object as the second argument`
                    )
                    return { success: false }
                }

                // Check if Fluent API tools are defined
                const toolsShape = definitionArg.get('tools')
                const hasFluentApiTools = toolsShape && !toolsShape.isUndefined()

                // If Fluent API tools are defined, check for Record API tool records
                // This prevents conflicts when unsupported tools exist from a previous transform.
                // Pass the skill's capability ID so we only flag Record API files belonging
                // to THIS skill, not other skills in the same app.
                if (hasFluentApiTools) {
                    const $idShape = definitionArg.get('$id')
                    const skillCapabilityId = $idShape?.getValue()?.toString()
                    const staleToolFiles = detectRecordApiToolRecords(compiler, skillCapabilityId)
                    if (staleToolFiles.length > 0) {
                        const rootDir = context.project.getRootDir()
                        const relativeFiles = staleToolFiles.map((f) => path.relative(rootDir, f))
                        const fileList = relativeFiles.map((f) => `  - ${f}`).join('\n')
                        diagnostics.error(
                            callExpression,
                            'Build conflict: Tools are defined in both the Fluent API and as Record API files.\n' +
                                'The following Record API files contain tool records:\n' +
                                fileList +
                                '\n\n' +
                                'To resolve this conflict:\n' +
                                '  1. If these files are leftover from a previous transform that included unsupported tools,\n' +
                                '     delete the files listed above and rebuild.\n' +
                                '  2. If unsupported tools (e.g., DocIntel, PI, Retriever) are still present in the skill,\n' +
                                '     remove them from the Now Assist Skill Kit UI, then re-transform before defining\n' +
                                '     tools in the Fluent API. Alternatively, remove the `tools` property from the\n' +
                                '     Fluent API to continue using Record API tools.'
                        )
                        return { success: false }
                    }
                }

                // Process skill using the imported processor
                return processSkill(callExpression, definitionArg, promptConfigArg, context)
            },
        },
        {
            shape: ArrowFunctionShape,
            async commit(shape, target, { commit }) {
                const creator = (shape.getSource() as Product).getCreator()
                if (!creator || creator.getName() !== 'NowAssistSkillPlugin') {
                    return { success: false }
                }

                const arrowFunction = ts.Node.isArrowFunction(target)
                    ? target
                    : target.replaceWithText('() => {}').asKindOrThrow(ts.SyntaxKind.ArrowFunction)

                const nodeStatements = arrowFunction.getStatements().filter((s) => !ts.Node.isReturnStatement(s))
                const shapeStatements = shape.getStatements()
                const returnValue = shape.getReturnValue()
                const totalExpected = shapeStatements.length
                const excess = nodeStatements.length - totalExpected
                for (let i = 1; i <= excess; i++) {
                    arrowFunction.removeStatement(nodeStatements.length - i)
                }

                for (const [i, statement] of shapeStatements.entries()) {
                    const targetStatement = nodeStatements[i] ?? arrowFunction.addStatements('undefined')[0]!
                    await commit(statement, targetStatement)
                }

                const targetReturn = arrowFunction
                    .getStatements()
                    .find((s) => ts.Node.isNode(s) && ts.Node.isReturnStatement(s))
                const targetReturnIdx = arrowFunction
                    .getStatements()
                    .findIndex((s) => ts.Node.isNode(s) && ts.Node.isReturnStatement(s))

                if (returnValue) {
                    if (targetReturn) {
                        arrowFunction.removeStatement(targetReturnIdx)
                    }

                    arrowFunction.addStatements(`return  ${returnValue.getCode()}`)
                }

                return { success: true }
            },
        },
    ],
})
